// SCR-STAFF-415 (بخش طرح‌های کیف پول؛ US-BIL-092): طرح‌های اعتبار هدیه شارژ کیف پول — حداقل شارژ، مبلغ هدیه، اعتبار به روز و بازه فعال‌بودن
// (bil.wallet.promotion.manage). هدیه فقط با شارژ پرداخت‌شده تعلق می‌گیرد و در سررسید با رویداد انقضا از کیف پول کسر می‌شود.
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilWalletPromotionView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, JalaliDatePickerRHF, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { parseRials } from './CashierPage';
import { Amount, bilKeys, rial } from './common';

interface Draft {
  id: string | null;
  name: string;
  minTopUp: string;
  gift: string;
  days: string;
  isActive: boolean;
}

export function WalletPromotionsSection() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [draft, setDraft] = useState<Draft | null>(null);
  const dates = useForm<{ from: string | null; to: string | null }>({ defaultValues: { from: null, to: null } });
  const promotions = useQuery({ queryKey: bilKeys.promotions(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/wallet-promotions')) });
  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        name: value.name.trim(),
        minTopUp: rial(parseRials(value.minTopUp)),
        giftAmount: rial(parseRials(value.gift)),
        validityDays: parseRials(value.days),
        validFrom: dates.getValues('from'),
        validTo: dates.getValues('to'),
        isActive: value.isActive,
      };
      return value.id
        ? unwrap(tenantApi.PUT('/api/v1/billing/wallet-promotions/{id}', { params: { path: { id: value.id } }, body }))
        : unwrap(tenantApi.POST('/api/v1/billing/wallet-promotions', { body, headers: { 'Idempotency-Key': key.current() } }));
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: bilKeys.promotions(session?.tenantId) });
    },
  });
  const open = (row: BilWalletPromotionView | null) => {
    dates.reset({ from: row?.validFrom ?? null, to: row?.validTo ?? null });
    setDraft(
      row
        ? { id: row.id, name: row.name, minTopUp: String(row.minTopUp.amount), gift: String(row.giftAmount.amount), days: String(row.validityDays), isActive: row.isActive }
        : { id: null, name: '', minTopUp: '', gift: '', days: '90', isActive: true },
    );
  };
  const columns: DataTableColumn<BilWalletPromotionView>[] = [
    { id: 'name', headerKey: 'bil:promotions.name', cell: (row) => row.name },
    { id: 'min', headerKey: 'bil:promotions.min_top_up', cell: (row) => <Amount money={row.minTopUp} /> },
    { id: 'gift', headerKey: 'bil:promotions.gift', cell: (row) => <Amount money={row.giftAmount} /> },
    { id: 'days', headerKey: 'bil:promotions.validity_days', cell: (row) => faDigits(String(row.validityDays)) },
    {
      id: 'window',
      headerKey: 'bil:promotions.window',
      cell: (row) => (
        <>
          {row.validFrom ? <JalaliDate value={row.validFrom} /> : '—'} – {row.validTo ? <JalaliDate value={row.validTo} /> : '—'}
        </>
      ),
    },
    { id: 'status', headerKey: 'bil:deposits.columns.status', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} /> },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => open(row)} data-testid="bil-promotion-edit">
          {t('bil:promotions.edit')}
        </Button>
      ),
    },
  ];
  const valid = draft !== null && draft.name.trim() !== '' && parseRials(draft.minTopUp) > 0 && parseRials(draft.gift) > 0 && parseRials(draft.days) > 0;
  return (
    <Stack spacing={1} data-testid="bil-promotions">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">{t('bil:promotions.title')}</Typography>
        <Button variant="outlined" onClick={() => open(null)} data-testid="bil-promotion-new">
          {t('bil:promotions.new')}
        </Button>
      </Stack>
      <DataTable
        captionKey="bil:promotions.title"
        columns={columns}
        rows={promotions.data?.items}
        getRowId={(row) => row.id}
        isLoading={promotions.isPending}
        error={promotions.error}
        onRetry={() => void promotions.refetch()}
        emptyTitleKey="bil:promotions.empty"
      />
      <FormDialog
        open={draft !== null}
        titleKey={draft?.id ? 'bil:promotions.edit' : 'bil:promotions.new'}
        onSubmit={() => draft && valid && save.mutate(draft)}
        onClose={() => setDraft(null)}
        isDirty={draft?.name !== ''}
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        {draft ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField required label={t('bil:promotions.name')} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'bil-promotion-name' } }} />
            <TextField
              required
              label={t('bil:promotions.min_top_up')}
              value={draft.minTopUp}
              onChange={(event) => setDraft({ ...draft, minTopUp: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-promotion-min' } }}
            />
            <TextField
              required
              label={t('bil:promotions.gift')}
              value={draft.gift}
              onChange={(event) => setDraft({ ...draft, gift: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-promotion-gift' } }}
            />
            <TextField
              required
              label={t('bil:promotions.validity_days')}
              value={draft.days}
              onChange={(event) => setDraft({ ...draft, days: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
            />
            <JalaliDatePickerRHF control={dates.control} name="from" labelKey="bil:promotions.valid_from" />
            <JalaliDatePickerRHF control={dates.control} name="to" labelKey="bil:promotions.valid_to" />
            <FormControlLabel control={<Switch checked={draft.isActive} onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })} />} label={t('bil:registers.active')} />
          </Stack>
        ) : null}
      </FormDialog>
    </Stack>
  );
}
